import {defineStore} from "pinia";
import {isEmpty} from "lodash";

export const usePiniaStore = defineStore('piniaStore', {
    state: () => ({
        isLoading: false,
        cart: {},
        customer: {},
        favorites: {},
        gifts: {},
        latest_order: {},
        user: {},
        jwtToken: null,
        language: null,
    }),

    actions: {
        async getUserData() {
            this.isLoading = true;

            this.language = drupalSettings.ef2_custom.language;

            await this.fetchToken();

            if (isEmpty(this.parsedToken)) {
                return;
            }

            axios.get(`${drupalSettings.ef2_custom.api_url}/user?token=${this.parsedToken}`).then((response) => {
                this.cart = response.data.data.cart;
                this.customer = response.data.data.customer;
                this.favorites = response.data.data.favorites;
                this.gifts = response.data.data.gifts;
                this.latest_order = response.data.data.orders.latest;
                this.user = response.data.data.user;
            }).catch((error) => {
                console.log('error', error)
            }).finally(() => {
                this.isLoading = false;
            });
        },

        updateUserData(data) {
            this.cart = data.cart;
            this.customer = data.customer;
            this.favorites = data.favorites;
            this.gifts = data.gifts;
            this.latest_order = data.orders.latest;
            this.user = data.user;
        },

        jwtTokenExpired() {
            let jwtToken = sessionStorage.getItem(drupalSettings.ef2_custom.customer_number)

            if (isEmpty(jwtToken)) {
                return true;
            }

            let parsedToken = JSON.parse(jwtToken);

            return new Date(new Date().getTime() + (60000 * 5)) >= new Date(parsedToken.expiration_time)
        },

        async fetchToken(ignoreExpired = false) {
            if (!document.body.classList.contains('user-logged-in')) {
                return;
            }

            let sessionKey = btoa(Array(
                drupalSettings.ef2_custom.customer_number,
                drupalSettings.ef2_custom.language,
                drupalSettings.ef2_custom.email,
            ).join());

            this.jwtToken = sessionStorage.getItem(sessionKey);

            if (this.jwtTokenExpired() || ignoreExpired) {
                return axios.get('/jwt/token')
                    .then(response => {
                        const tokenObject = {
                            'token': response.data.token,
                            'expiration_time': new Date(new Date().getTime() + (60000 * 35)),
                        };

                        this.jwtToken = JSON.stringify(tokenObject);

                        sessionStorage.setItem(sessionKey, this.jwtToken);
                    })
                    .finally(() => {
                        const payload = {
                            jwtToken: this.jwtToken,
                            languageId: drupalSettings.ef2_custom.language,
                        }
                    });
            }
        },

    },

    getters: {
        magnumDiscountInfo: (state) => {
            return (itemSku, quantity, quantityPrices, unitPrice, totalDiscount = null) => {
                const magnumSku = state.customer?.magnum_sku;
                if (!magnumSku || itemSku != magnumSku) {
                    return null;
                }

                const magnumCounter = (state.cart?.lines?.[itemSku]?.magnum_counter !== undefined ? state.cart?.lines?.[itemSku]?.magnum_counter : state.customer?.magnum_counter) ?? 0;
                const freeQuantityEntry = Object.values(quantityPrices ?? {}).find(p => p.percentage == 100);

                if (freeQuantityEntry === undefined) {
                    return null;
                }

                const packageQuantity = freeQuantityEntry ? freeQuantityEntry.quantity : 20;
                const totalRemainingAllowance = Math.max(0, packageQuantity - magnumCounter);

                let freePackages;
                if (totalDiscount !== null) {
                    // CartRow logic: based on calculated discount
                    const divisor = unitPrice;
                    const freePackagesFromDiscount = divisor > 0 ? Math.round(totalDiscount / divisor) : 0;
                    freePackages = Math.min(freePackagesFromDiscount, totalRemainingAllowance);
                }
                else {
                    // ProductPrice logic: based on the selected quantity.
                    // Always show the maximum original quantity someone can order (e.g., up to 20), not capped by remaining allowance.
                    freePackages = packageQuantity;
                }

                const remainingPackages = Math.max(0, totalRemainingAllowance - quantity);

                return {
                    freePackages,
                    remainingPackages
                };
            };
        },

        magnumDiscountText: (state) => {
            return (info) => {
                if (!info) {
                    return '';
                }

                const { freePackages, remainingPackages } = info;

                const freeText = Drupal.formatPlural(freePackages, "@count free package", "@count free packages");
                const remainingText = Drupal.formatPlural(remainingPackages, "@count remaining", "@count remaining");

                return `${freeText} (${remainingText})`;
            };
        },

        parsedToken() {
            let stringifiedToken = this.jwtToken

            if (isEmpty(stringifiedToken)) {
                return;
            }

            let parsedToken = JSON.parse(stringifiedToken);

            return parsedToken.token;
        },
    }
})
